import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

/**
 * TikTok OAuth の Redirect URI 受け口。
 * ページ自体はトークン交換しない（シークレットをブラウザに出さない）。
 * 認可 code を表示し、開発PCの fetch スクリプトへ貼り付けできるようにする。
 * あわせて /api/oauth/tiktok/capture へ自動送信（エージェント回収用）。
 */
export default function TikTokOAuthCallback() {
  const [params] = useSearchParams();
  const [copied, setCopied] = useState<'url' | 'code' | null>(null);
  const [captureStatus, setCaptureStatus] = useState<'idle' | 'ok' | 'fail'>('idle');

  const code = (params.get('code') || '').trim();
  const state = (params.get('state') || '').trim();
  const error = (params.get('error') || '').trim();
  const errorDescription = (params.get('error_description') || '').trim();
  const fullUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.href;
  }, []);

  useEffect(() => {
    if (!code || code === '...' || !state) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/oauth/tiktok/capture', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state }),
        });
        if (!cancelled) setCaptureStatus(res.ok ? 'ok' : 'fail');
      } catch {
        if (!cancelled) setCaptureStatus('fail');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, state]);

  const copy = async (kind: 'url' | 'code', text: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
    } catch {
      setCopied(null);
    }
  };

  return (
    <Container className="py-5">
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
        <title>TikTok OAuth コールバック | kensuke-system</title>
      </Helmet>
      <Row className="justify-content-center">
        <Col md={8} lg={7}>
          <Card>
            <Card.Body className="p-4">
              <h1 className="h4 mb-3">TikTok 認可コールバック</h1>
              {error ? (
                <>
                  <p className="text-danger mb-2">認可に失敗しました。</p>
                  <Form.Control as="textarea" rows={3} readOnly value={`${error}\n${errorDescription}`} />
                </>
              ) : !code || code === '...' ? (
                <>
                  <p className="text-danger mb-2">
                    認可コード（code）がありません。チャットの例 URL（code=...）を開いた場合は無効です。
                  </p>
                  <p className="mb-0 small text-muted">
                    開発PCで <code>fetch-gaisuke-tiktok-refresh-token.mjs</code> が表示した認可 URL
                    からやり直してください。
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-3">
                    認可に成功しました。下の <strong>コールバック URL 全体</strong> をコピーし、ターミナルの{' '}
                    <code>Callback URL or code:</code> に貼り付けて Enter してください。
                    {captureStatus === 'ok' ? (
                      <span className="d-block text-success mt-2">サーバーへ code を送信済みです。このタブはそのままで構いません。</span>
                    ) : null}
                    {captureStatus === 'fail' ? (
                      <span className="d-block text-warning mt-2">自動送信に失敗しました。下の URL を手動でコピーしてください。</span>
                    ) : null}
                  </p>
                  <Form.Label className="fw-bold">コールバック URL（推奨）</Form.Label>
                  <Form.Control as="textarea" rows={4} readOnly value={fullUrl} className="mb-2 font-monospace small" />
                  <Button
                    type="button"
                    variant="primary"
                    className="me-2 mb-3"
                    onClick={() => void copy('url', fullUrl)}
                  >
                    {copied === 'url' ? 'コピーしました' : 'URL をコピー'}
                  </Button>
                  <Form.Label className="fw-bold">code のみ</Form.Label>
                  <Form.Control as="textarea" rows={3} readOnly value={code} className="mb-2 font-monospace small" />
                  <Button type="button" variant="outline-primary" onClick={() => void copy('code', code)}>
                    {copied === 'code' ? 'コピーしました' : 'code をコピー'}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
